import { 
  FirmProfile, 
  InvestmentThesis, 
  DecisionCriteria, 
  WorkflowProcess, 
  EvaluationTemplate, 
  FilterRule, 
  ThesisVersion 
} from '../../types';
import { mockUsers } from './users';

// Mock Firm Profile
export const mockFirmProfile: FirmProfile = {
  id: 'fp-001',
  firm_name: 'Open AnaCapital Partners',
  fund_name: 'OpenAnaOpportunity Fund III',
  sector_focus: ['Technology', 'Healthcare', 'Financial Services'],
  industry_focus: ['SaaS', 'Biotech', 'Fintech'],
  stage_focus: ['Growth', 'Late Stage', 'Buyout'],
  geographic_focus: ['North America', 'Western Europe'],
  investment_size_min: '10000000',
  investment_size_max: '50000000',
  holding_period_target: 5,
  holding_period_unit: 'years',
  exit_strategy_preferences: ['IPO', 'Strategic Sale', 'Secondary Buyout'],
  target_portfolio_mix: {
    'Technology': 40,
    'Healthcare': 30,
    'Financial Services': 20,
    'Other': 10
  },
  organization_id: 'org-123',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

// Mock Investment Thesis
export const mockInvestmentThesis: InvestmentThesis = {
  id: 'it-001',
  firm_profile_id: mockFirmProfile.id,
  core_investment_themes: [
    'Digital Transformation',
    'Healthcare Innovation',
    'Financial Inclusion',
    'Sustainable Business Models'
  ],
  value_creation_levers: [
    'Operational Efficiency',
    'Market Expansion',
    'Product Development',
    'M&A Integration',
    'Talent Acquisition'
  ],
  deal_breakers: [
    'Regulatory Uncertainty',
    'Customer Concentration > 30%',
    'Negative EBITDA for 3+ Years',
    'Litigation Risk'
  ],
  preferred_business_models: [
    'SaaS',
    'Subscription',
    'Marketplace',
    'Recurring Revenue'
  ],
  macro_trends: [
    'Aging Population',
    'Remote Work',
    'AI Adoption',
    'Climate Change Response'
  ],
  micro_trends: [
    'Telemedicine',
    'Cybersecurity',
    'Digital Payments',
    'Cloud Migration'
  ],
  risk_appetite: 'Medium',
  name: 'Global Thesis 2024',
  description: 'Global investment thesis for 2024',
  organization_id: 'org-123',
  is_global: true,
  firm_profile: mockFirmProfile,
  fund_id: null,
  version: 1,
  is_active: true,
  created_by: mockUsers[0].id,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  fund_name: null,
  creator_name: mockUsers[0].name
};

// Mock Decision Criteria
export const mockDecisionCriteria: DecisionCriteria = {
  id: 'dc-001',
  firmProfileId: mockFirmProfile.id, // This should be firm_profile_id
  categories: [
    {
      id: 'cat-001',
      name: 'Financial Metrics',
      weight: 40,
      criteria: [
        {
          id: 'crit-001',
          name: 'Revenue Range',
          description: 'Annual revenue range in USD',
          thresholdType: 'Range',
          thresholdMin: 10000000,
          thresholdMax: 100000000,
          isMustMeet: true,
          weight: 25
        },
        {
          id: 'crit-002',
          name: 'EBITDA Margin',
          description: 'Minimum acceptable EBITDA margin',
          thresholdType: 'Minimum',
          thresholdValue: 15,
          isMustMeet: true,
          weight: 25
        },
        {
          id: 'crit-003',
          name: 'Growth Rate',
          description: 'Annual revenue growth rate',
          thresholdType: 'Minimum',
          thresholdValue: 10,
          isMustMeet: false,
          weight: 25
        },
        {
          id: 'crit-004',
          name: 'IRR Target',
          description: 'Target internal rate of return',
          thresholdType: 'Minimum',
          thresholdValue: 20,
          isMustMeet: true,
          weight: 25
        }
      ]
    },
    {
      id: 'cat-002',
      name: 'Market Metrics',
      weight: 25,
      criteria: [
        {
          id: 'crit-005',
          name: 'TAM Size',
          description: 'Total addressable market size in USD',
          thresholdType: 'Minimum',
          thresholdValue: 1000000000,
          isMustMeet: true,
          weight: 30
        },
        {
          id: 'crit-006',
          name: 'Market Growth Rate',
          description: 'Annual market growth rate',
          thresholdType: 'Minimum',
          thresholdValue: 5,
          isMustMeet: false,
          weight: 30
        },
        {
          id: 'crit-007',
          name: 'Competition Intensity',
          description: 'Level of competition in the market',
          thresholdType: 'Options',
          options: ['Low', 'Medium', 'High'],
          thresholdValue: 'Medium',
          isMustMeet: false,
          weight: 40
        }
      ]
    },
    {
      id: 'cat-003',
      name: 'Risk Assessment',
      weight: 15,
      criteria: [
        {
          id: 'crit-008',
          name: 'Regulatory Exposure',
          description: 'Level of regulatory exposure',
          thresholdType: 'Options',
          options: ['Low', 'Medium', 'High'],
          thresholdValue: 'Medium',
          isMustMeet: false,
          weight: 30
        },
        {
          id: 'crit-009',
          name: 'Customer Concentration',
          description: 'Percentage of revenue from top 3 customers',
          thresholdType: 'Maximum',
          thresholdValue: 30,
          isMustMeet: true,
          weight: 40
        },
        {
          id: 'crit-010',
          name: 'Geographic Risk',
          description: 'Level of geographic risk',
          thresholdType: 'Options',
          options: ['Low', 'Medium', 'High'],
          thresholdValue: 'Medium',
          isMustMeet: false,
          weight: 30
        }
      ]
    },
    {
      id: 'cat-004',
      name: 'Operational Metrics',
      weight: 10,
      criteria: [
        {
          id: 'crit-011',
          name: 'Management Experience',
          description: 'Years of relevant industry experience',
          thresholdType: 'Minimum',
          thresholdValue: 5,
          isMustMeet: false,
          weight: 50
        },
        {
          id: 'crit-012',
          name: 'Scalability Potential',
          description: 'Potential for operational scaling',
          thresholdType: 'Options',
          options: ['Low', 'Medium', 'High'],
          thresholdValue: 'High',
          isMustMeet: false,
          weight: 50
        }
      ]
    },
    {
      id: 'cat-005',
      name: 'ESG Criteria',
      weight: 10,
      criteria: [
        {
          id: 'crit-013',
          name: 'Environmental Impact',
          description: 'Level of environmental impact',
          thresholdType: 'Options',
          options: ['Negative', 'Neutral', 'Positive'],
          thresholdValue: 'Neutral',
          isMustMeet: false,
          weight: 30
        },
        {
          id: 'crit-014',
          name: 'Social Responsibility',
          description: 'Level of social responsibility',
          thresholdType: 'Options',
          options: ['Low', 'Medium', 'High'],
          thresholdValue: 'Medium',
          isMustMeet: false,
          weight: 30
        },
        {
          id: 'crit-015',
          name: 'Governance Quality',
          description: 'Quality of corporate governance',
          thresholdType: 'Options',
          options: ['Low', 'Medium', 'High'],
          thresholdValue: 'High',
          isMustMeet: true,
          weight: 40
        }
      ]
    }
  ],
  lastUpdated: new Date().toISOString(),
  updatedBy: mockUsers[0].id
};

// Mock Workflow Process
export const mockWorkflowProcess: WorkflowProcess = {
  id: 'wp-001',
  firmProfileId: mockFirmProfile.id,
  stages: [
    {
      id: 'stage-001',
      name: 'Initial Screening',
      order: 1,
      entryRules: ['New deal submission'],
      exitRules: ['Meets basic criteria', 'Approved by analyst'],
      roleAssignments: ['Investment Analyst'],
      stageSLA: 5,
      stageSLAUnit: 'days',
      approvalRule: 'majority',
      isActive: true
    },
    {
      id: 'stage-002',
      name: 'Preliminary Due Diligence',
      order: 2,
      entryRules: ['Passed initial screening'],
      exitRules: ['Financial review complete', 'Market analysis complete'],
      roleAssignments: ['Investment Analyst', 'VP'],
      stageSLA: 14,
      stageSLAUnit: 'days',
      approvalRule: 'majority',
      isActive: true
    },
    {
      id: 'stage-003',
      name: 'Investment Committee Review',
      order: 3,
      entryRules: ['Due diligence complete', 'Investment memo prepared'],
      exitRules: ['IC approval'],
      roleAssignments: ['VP', 'Partner'],
      stageSLA: 7,
      stageSLAUnit: 'days',
      approvalRule: 'unanimous',
      isActive: true
    },
    {
      id: 'stage-004',
      name: 'Final Due Diligence',
      order: 4,
      entryRules: ['IC approval for detailed review'],
      exitRules: ['Legal review complete', 'Financial audit complete'],
      roleAssignments: ['Investment Analyst', 'VP', 'External Advisors'],
      stageSLA: 30,
      stageSLAUnit: 'days',
      approvalRule: 'majority',
      isActive: true
    },
    {
      id: 'stage-005',
      name: 'Deal Execution',
      order: 5,
      entryRules: ['Final due diligence complete', 'Final IC approval'],
      exitRules: ['Deal closed'],
      roleAssignments: ['VP', 'Partner', 'Legal'],
      stageSLA: 14,
      stageSLAUnit: 'days',
      approvalRule: 'unanimous',
      isActive: true
    }
  ],
  lastUpdated: new Date().toISOString(),
  updatedBy: mockUsers[0].id
};

// Mock Evaluation Templates
export const mockEvaluationTemplates: EvaluationTemplate[] = [
  {
    id: 'et-001',
    firmProfileId: mockFirmProfile.id,
    name: 'Initial Deal Screening',
    type: 'Initial Screening',
    sections: [
      {
        id: 'section-001',
        name: 'Deal Overview',
        order: 1,
        fields: [
          {
            id: 'field-001',
            name: 'Company Name',
            type: 'Text',
            required: true,
            order: 1
          },
          {
            id: 'field-002',
            name: 'Industry',
            type: 'Dropdown',
            options: ['Technology', 'Healthcare', 'Financial Services', 'Consumer', 'Industrial', 'Other'],
            required: true,
            order: 2
          },
          {
            id: 'field-003',
            name: 'Deal Size (USD)',
            type: 'Number',
            required: true,
            order: 3
          },
          {
            id: 'field-004',
            name: 'Geography',
            type: 'MultiSelect',
            options: ['North America', 'South America', 'Europe', 'Asia', 'Africa', 'Australia'],
            required: true,
            order: 4
          }
        ]
      },
      {
        id: 'section-002',
        name: 'Financial Snapshot',
        order: 2,
        fields: [
          {
            id: 'field-005',
            name: 'Annual Revenue (USD)',
            type: 'Number',
            required: true,
            order: 1
          },
          {
            id: 'field-006',
            name: 'EBITDA Margin (%)',
            type: 'Number',
            required: true,
            order: 2
          },
          {
            id: 'field-007',
            name: 'YoY Growth (%)',
            type: 'Number',
            required: true,
            order: 3
          },
          {
            id: 'field-008',
            name: 'Profitable',
            type: 'Checkbox',
            required: true,
            order: 4
          }
        ]
      },
      {
        id: 'section-003',
        name: 'Strategic Fit',
        order: 3,
        fields: [
          {
            id: 'field-009',
            name: 'Alignment with Investment Thesis',
            type: 'Radio',
            options: ['Low', 'Medium', 'High'],
            required: true,
            order: 1
          },
          {
            id: 'field-010',
            name: 'Potential Value Creation',
            type: 'MultiSelect',
            options: ['Operational Efficiency', 'Market Expansion', 'Product Development', 'M&A Integration', 'Talent Acquisition'],
            required: true,
            order: 2
          },
          {
            id: 'field-011',
            name: 'Initial Concerns',
            type: 'Textarea',
            required: false,
            order: 3
          }
        ]
      }
    ],
    lastUpdated: new Date().toISOString(),
    updatedBy: mockUsers[0].id
  },
  {
    id: 'et-002',
    firmProfileId: mockFirmProfile.id,
    name: 'Detailed Evaluation Form',
    type: 'Detailed Evaluation',
    sections: [
      {
        id: 'section-004',
        name: 'Market Analysis',
        order: 1,
        fields: [
          {
            id: 'field-012',
            name: 'TAM Size (USD)',
            type: 'Number',
            required: true,
            order: 1
          },
          {
            id: 'field-013',
            name: 'Market Growth Rate (%)',
            type: 'Number',
            required: true,
            order: 2
          },
          {
            id: 'field-014',
            name: 'Competitive Landscape',
            type: 'Textarea',
            required: true,
            order: 3
          },
          {
            id: 'field-015',
            name: 'Market Position',
            type: 'Radio',
            options: ['Leader', 'Challenger', 'Follower', 'Niche Player'],
            required: true,
            order: 4
          }
        ]
      },
      {
        id: 'section-005',
        name: 'Business Model Assessment',
        order: 2,
        fields: [
          {
            id: 'field-016',
            name: 'Revenue Model',
            type: 'MultiSelect',
            options: ['Subscription', 'Transaction', 'Licensing', 'Advertising', 'Services', 'Hardware'],
            required: true,
            order: 1
          },
          {
            id: 'field-017',
            name: 'Customer Acquisition Cost (USD)',
            type: 'Number',
            required: true,
            order: 2
          },
          {
            id: 'field-018',
            name: 'Lifetime Value (USD)',
            type: 'Number',
            required: true,
            order: 3
          },
          {
            id: 'field-019',
            name: 'Recurring Revenue (%)',
            type: 'Number',
            required: true,
            order: 4
          }
        ]
      }
    ],
    lastUpdated: new Date().toISOString(),
    updatedBy: mockUsers[0].id
  },
  {
    id: 'et-003',
    firmProfileId: mockFirmProfile.id,
    name: 'Red Flag Checklist',
    type: 'Red Flag Checklist',
    sections: [
      {
        id: 'section-006',
        name: 'Financial Red Flags',
        order: 1,
        fields: [
          {
            id: 'field-020',
            name: 'Declining Revenue',
            type: 'Checkbox',
            required: true,
            order: 1
          },
          {
            id: 'field-021',
            name: 'Negative Cash Flow',
            type: 'Checkbox',
            required: true,
            order: 2
          },
          {
            id: 'field-022',
            name: 'High Debt Level',
            type: 'Checkbox',
            required: true,
            order: 3
          },
          {
            id: 'field-023',
            name: 'Accounting Irregularities',
            type: 'Checkbox',
            required: true,
            order: 4
          }
        ]
      },
      {
        id: 'section-007',
        name: 'Operational Red Flags',
        order: 2,
        fields: [
          {
            id: 'field-024',
            name: 'High Customer Churn',
            type: 'Checkbox',
            required: true,
            order: 1
          },
          {
            id: 'field-025',
            name: 'Key Person Dependency',
            type: 'Checkbox',
            required: true,
            order: 2
          },
          {
            id: 'field-026',
            name: 'Supply Chain Risks',
            type: 'Checkbox',
            required: true,
            order: 3
          },
          {
            id: 'field-027',
            name: 'Technology Obsolescence Risk',
            type: 'Checkbox',
            required: true,
            order: 4
          }
        ]
      }
    ],
    lastUpdated: new Date().toISOString(),
    updatedBy: mockUsers[0].id
  }
];

// Mock Filter Rules
export const mockFilterRules: FilterRule[] = [
  {
    id: 'fr-001',
    firmProfileId: mockFirmProfile.id,
    name: 'Auto-Reject Small Deals',
    description: 'Automatically reject deals with a size less than $5M',
    filter_type: 'Auto-Reject',
    conditions: [
      {
        id: 'cond-001',
        field: 'Deal Size',
        operator: 'less_than',
        value: 5000000
      }
    ],
    action: 'Reject and notify analyst',
    is_active: true,
    lastUpdated: new Date().toISOString(),
    updatedBy: mockUsers[0].id
  },
  {
    id: 'fr-002',
    firmProfileId: mockFirmProfile.id,
    name: 'Highlight High Growth',
    description: 'Highlight deals with a YoY growth greater than 30%',
    filter_type: 'Highlight',
    conditions: [
      {
        id: 'cond-002',
        field: 'YoY Growth',
        operator: 'greater_than',
        value: 30
      }
    ],
    action: 'Highlight in green and flag for VP review',
    is_active: true,
    lastUpdated: new Date().toISOString(),
    updatedBy: mockUsers[0].id
  },
  {
    id: 'fr-003',
    firmProfileId: mockFirmProfile.id,
    name: 'Auto-Accept Strategic Sectors',
    description: 'Auto-accept deals in strategic sectors with high EBITDA margins',
    filter_type: 'Auto-Accept',
    conditions: [
      {
        id: 'cond-003',
        field: 'Industry',
        operator: 'equals',
        value: 'Technology'
      },
      {
        id: 'cond-004',
        field: 'EBITDA Margin',
        operator: 'greater_than',
        value: 25,
        logicalOperator: 'AND'
      }
    ],
    action: 'Fast-track to preliminary due diligence',
    is_active: true,
    lastUpdated: new Date().toISOString(),
    updatedBy: mockUsers[0].id
  }
];

// Mock Version History
export const mockThesisVersions: ThesisVersion[] = [
  {
    id: 'tv-001',
    firmProfileId: mockFirmProfile.id,
    versionNumber: 1,
    timestamp: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString(),
    userId: mockUsers[0].id,
    userName: mockUsers[0].name,
    changes: [
      {
        id: 'change-001',
        section: 'Firm Profile',
        field: 'Initial Creation',
        oldValue: null,
        newValue: 'Created firm profile'
      }
    ],
    rationale: 'Initial setup of investment thesis'
  },
  {
    id: 'tv-002',
    firmProfileId: mockFirmProfile.id,
    versionNumber: 2,
    timestamp: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString(),
    userId: mockUsers[1].id,
    userName: mockUsers[1].name,
    changes: [
      {
        id: 'change-002',
        section: 'Investment Thesis',
        field: 'Core Investment Themes',
        oldValue: JSON.stringify(['Digital Transformation', 'Healthcare Innovation', 'Financial Inclusion']),
        newValue: JSON.stringify(['Digital Transformation', 'Healthcare Innovation', 'Financial Inclusion', 'Sustainable Business Models'])
      },
      {
        id: 'change-003',
        section: 'Decision Criteria',
        field: 'ESG Criteria',
        oldValue: 'Weight: 5',
        newValue: 'Weight: 10'
      }
    ],
    rationale: 'Updated to reflect increased focus on sustainability and ESG factors'
  },
  {
    id: 'tv-003',
    firmProfileId: mockFirmProfile.id,
    versionNumber: 3,
    timestamp: new Date().toISOString(),
    userId: mockUsers[2].id,
    userName: mockUsers[2].name,
    changes: [
      {
        id: 'change-004',
        section: 'Firm Profile',
        field: 'Investment Size Range',
        oldValue: '$5M - $30M',
        newValue: '$10M - $50M'
      },
      {
        id: 'change-005',
        section: 'Workflow Process',
        field: 'Due Diligence Stage',
        oldValue: 'SLA: 21 days',
        newValue: 'SLA: 14 days'
      }
    ],
    rationale: 'Adjusted to reflect larger fund size and streamlined due diligence process'
  }
];

// Export all mock data
export const mockInvestmentThesisData = {
  firmProfile: mockFirmProfile,
  investmentThesis: mockInvestmentThesis,
  decisionCriteria: mockDecisionCriteria,
  workflowProcess: mockWorkflowProcess,
  evaluationTemplates: mockEvaluationTemplates,
  filterRules: mockFilterRules,
  thesisVersions: mockThesisVersions
};
export const commonSectors = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Consumer Discretionary',
  'Industrials',
  'Real Estate',
  'Energy',
  'Utilities',
  'Materials',
  'Telecommunication Services',
];
